import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Controller, useFormContext } from 'react-hook-form';
import useIsFieldRequired from '../../../utility/useIsFieldRequired';

// MUI's Select forwards its `value` straight through to a hidden native <input>, and React
// disallows a null value prop there ("`value` prop on `input` should not be null") — but some
// RMSSelect consumers use a real `null` option value (e.g. an "All ___" option). NULL_SENTINEL is
// a DOM-facing stand-in for null so the native input never sees it; it's translated back to null
// before it ever reaches react-hook-form or the `onChange` prop, so stored form values and
// existing consumers are unaffected.
const NULL_SENTINEL = '__rms_select_null__';
const toWireValue = (value) => (value === null ? NULL_SENTINEL : value);
const fromWireValue = (value) => (value === NULL_SENTINEL ? null : value);

const RMSSelect = ({
  name,
  label,
  options = [],
  disabled = false,
  optionLabel = 'label',
  optionValue = 'value',
  fullWidth = true,
  onChange,
  prefixIcon,
  onPrefixIconClick,
  showLabelOnly = true,
  size = 'small',
  className,
  // Defaults to false so every existing RMSSelect usage keeps its current look — opt in
  // per-field where a quick way to clear the selection (matching RMSAutocomplete's native
  // clear button) is actually wanted.
  clearable = false,
  // MUI's Select only renders a matched MenuItem's label when its value is "filled" (MUI's own
  // check, which treats null/''/undefined as not filled) — otherwise it shows blank even though
  // the value correctly matches an option (e.g. an "All ___" option with value: null). Defaults to
  // false so every existing RMSSelect usage is unaffected; pass true only when an option's real
  // value is null/empty and needs to display.
  displayEmpty = false,
  // Lets a consumer control how an option is arranged in the open menu list (e.g. bolding a
  // code, showing extra fields) without RMSSelect needing to know that shape. Defaults to the
  // plain label — most dropdowns don't need anything fancier.
  renderOption,
  // Lets a consumer control what the closed/collapsed field shows for the selected option,
  // independently of renderOption (e.g. list shows "Code - Description" but the collapsed
  // field shows just "Code"). Defaults to mirroring renderOption, same as a native <select>.
  getSelectedLabel,
}) => {
  const { control } = useFormContext();
  const isRequired = useIsFieldRequired(name);

  const resolveOptionContent = (option) => {
    if (renderOption) return renderOption(option);

    if (!showLabelOnly) {
      return (
        <>
          <strong>{option?.[optionValue]}</strong>
          {option?.[optionLabel] ? <>&nbsp;-&nbsp;{option[optionLabel]}</> : null}
        </>
      );
    }

    return option?.[optionLabel];
  };

  const resolveSelectedLabel = (option) => {
    if (getSelectedLabel) return getSelectedLabel(option);

    return resolveOptionContent(option);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // An empty string is RMSSelect's own "nothing selected" sentinel (it's what the blank
        // placeholder MenuItem carries, and what onChange normalizes back to null) — so it must
        // never count as a real match here, even if some dictionary happens to contain a row
        // with a blank Code. Otherwise the label shows shrunk/floated over an empty box instead
        // of resting in its normal, unselected position.
        const hasValue =
          field.value !== '' && options?.some((option) => option?.[optionValue] === field.value);

        const isDisabled = disabled || field.disabled;

        return (
          <FormControl
            variant="outlined"
            fullWidth={fullWidth}
            error={!!error}
            required={isRequired}
            size={size}
            className={className}>
            {/* MUI auto-shrinks the label based on its own "filled" check, which treats a
                selected value of null (e.g. an "All ___" option) as not filled — so the label
                would stay in its resting position and visually sit on top of the displayed
                value instead of floating above it. hasValue already reflects the real
                selection state (including null-for-All), so it drives shrink directly instead
                of relying on MUI's auto-detection. */}
            <InputLabel shrink={hasValue || undefined}>{label}</InputLabel>

            <Select
              {...field}
              label={label}
              disabled={isDisabled}
              displayEmpty={displayEmpty}
              MenuProps={{ classes: { paper: 'rms-select-menu-paper' } }}
              value={hasValue ? toWireValue(field.value) : ''}
              renderValue={(wireValue) => {
                const rawValue = fromWireValue(wireValue);
                const selectedOption = options.find((option) => option?.[optionValue] === rawValue);

                return resolveSelectedLabel(selectedOption);
              }}
              input={
                <OutlinedInput
                  label={label}
                  startAdornment={
                    prefixIcon ? (
                      <InputAdornment position="start">
                        <Box
                          display="flex"
                          alignItems="center"
                          onClick={(e) => {
                            e.stopPropagation();

                            if (onPrefixIconClick && !isDisabled) {
                              onPrefixIconClick();
                            }
                          }}
                          style={{
                            cursor: onPrefixIconClick && !isDisabled ? 'pointer' : 'default',
                            opacity: isDisabled ? 0.5 : 1,
                          }}>
                          {prefixIcon}
                        </Box>
                      </InputAdornment>
                    ) : null
                  }
                  endAdornment={
                    clearable && hasValue ? (
                      <InputAdornment position="end" className="rms-select__clear-adornment">
                        <IconButton
                          size="small"
                          aria-label={`Clear ${label}`}
                          disabled={isDisabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            field.onChange(null);

                            if (onChange) {
                              onChange(e, null);
                            }
                          }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                />
              }
              onChange={(e) => {
                // The blank "Select ___" placeholder below always has value="" — selecting it
                // means "clear this field", so it must resolve to null like every other cleared
                // value here. Left as '' instead, Yup's number schemas cast it to NaN rather than
                // null, so a nullable numeric field (e.g. an *ID select) throws "must be a number
                // type, but the final value was NaN" the moment this placeholder is picked.
                const rawValue = e.target.value === '' ? null : fromWireValue(e.target.value);
                e.target.value = rawValue;
                field.onChange(rawValue);

                const selectedOption =
                  options.find((option) => option?.[optionValue] === rawValue) || null;

                if (onChange) {
                  onChange(e, selectedOption);
                }
              }}>
              <MenuItem value="">
                <em>Select {label}</em>
              </MenuItem>

              {options.map((option) => (
                <MenuItem key={option?.[optionValue]} value={toWireValue(option?.[optionValue])}>
                  {resolveOptionContent(option)}
                </MenuItem>
              ))}
            </Select>

            <FormHelperText>{error?.message}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};

export default RMSSelect;
